// @ts-check
// "Enviar teste" do modelo (spec/email.md seção 8.6; spec-2.0.0 K42): sempre para quem pede e, se quiser, para até 4
// colegas da equipe (nunca para um endereço digitado). Com um card escolhido, só quem pede recebe os dados dele; os
// colegas recebem os dados de exemplo. Até 20 testes por pessoa por hora.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { sendEmailTemplateTest } from '../../data/email-templates.js';
import { ApiError } from '../../data/api-client.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { currentWorkspaceMemberState } from '../../data/metadata-store.js';
import { Button } from '../../ui/button.js';
import { Checkbox } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { enqueueSnackbar, showErrorSnackbar } from '../../ui/snackbar.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { formatTime } from '../../lib/format.js';
import { EmailDialog } from './email-dialog.js';
import { SampleCardPicker } from './email-preview-frame.js';
import { t } from '../../i18n/index.js';

export const MAXIMUM_TEST_COLLEAGUES = 4;

/** @param {unknown} error */
export function testErrorMessage(error) {
  if (error instanceof ApiError && error.code === 'TEST_LIMIT_EXCEEDED') {
    const retryAt = typeof error.details?.retryAt === 'string' ? formatTime(error.details.retryAt) : '';
    return retryAt ? t('emailTemplates.test.limitUntil', { time: retryAt }) : t('emailTemplates.test.limit');
  }
  return null;
}

/**
 * @param {{ templateId: string, templateName: string, hasErrors: boolean, onClose: () => void }} props
 */
export function EmailTemplateTestDialog({ templateId, templateName, hasErrors, onClose }) {
  const currentMember = currentWorkspaceMemberState.value;
  const [colleagueIds, setColleagueIds] = useState(/** @type {string[]} */ ([]));
  const [card, setCard] = useState(/** @type {{ id: string, label: string } | null} */ (null));
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    loadWorkspaceMembers();
  }, []);
  const colleagues = (workspaceMembersState.value ?? []).filter((member) => member.id !== currentMember?.id);

  const send = async () => {
    setIsSending(true);
    setError('');
    try {
      const result = await sendEmailTemplateTest(templateId, { sampleCardId: card?.id ?? null, workspaceMemberIds: colleagueIds });
      // no modo demonstração nada sai de verdade: o aviso diz que foi simulado
      enqueueSnackbar({ variant: 'success', message: t(result.simulated ? 'emailTemplates.test.simulated' : 'emailTemplates.test.sent', { list: result.sentTo.join(', ') }) });
      onClose();
    } catch (sendError) {
      const message = testErrorMessage(sendError);
      if (message) setError(message);
      else showErrorSnackbar(sendError);
    } finally {
      setIsSending(false);
    }
  };

  return html`<${EmailDialog} title=${t('emailTemplates.test.title')} subtitle=${templateName} onClose=${onClose} className="email-test-dialog" testId="email-test-dialog"
    footer=${html`
      <${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('emailTemplates.test.send')} icon="IconSend" accent="blue" size="small" isLoading=${isSending} disabled=${hasErrors} onClick=${send} />
    `}>
    ${hasErrors ? html`<p class="email-inline-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} />${t('emailTemplates.test.hasErrors')}</p>` : null}
    <div class="email-test-recipients">
      <label class="email-check-row" data-disabled="true">
        <${Checkbox} checked=${true} disabled=${true} ariaLabel=${t('emailTemplates.test.toMe')} />
        <span>${t('emailTemplates.test.toMe')}${currentMember?.userEmail ? html` <span class="email-muted">(${currentMember.userEmail})</span>` : null}</span>
      </label>
      <p class="email-section-label">${t('emailTemplates.test.toMembers', { count: MAXIMUM_TEST_COLLEAGUES })}</p>
      <ul class="email-member-list">
        ${colleagues.map((member) => {
          const checked = colleagueIds.includes(member.id);
          const disabled = !checked && colleagueIds.length >= MAXIMUM_TEST_COLLEAGUES;
          return html`<li key=${member.id}>
            <label class="email-check-row" data-disabled=${disabled ? 'true' : undefined}>
              <${Checkbox} checked=${checked} disabled=${disabled} ariaLabel=${formatFullName(member.name) || member.userEmail || ''}
                onChange=${(/** @type {boolean} */ next) => setColleagueIds((current) => (next ? [...current, member.id] : current.filter((id) => id !== member.id)))} />
              <span>${formatFullName(member.name) || member.userEmail}</span>
              ${member.userEmail ? html`<span class="email-muted">${member.userEmail}</span>` : null}
            </label>
          </li>`;
        })}
      </ul>
    </div>
    <${SampleCardPicker} value=${card} onChange=${setCard} />
    <p class="input-hint">${card ? t('emailTemplates.test.cardOnlyForMe') : t('emailTemplates.test.sampleHint')}</p>
    <p class="input-hint">${t('emailTemplates.test.limitHint')}</p>
    ${error ? html`<p class="input-hint" data-danger="true" role="alert">${error}</p>` : null}
  <//>`;
}
